import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { FaPlus, FaSave, FaTimes, FaRegStickyNote, FaCheck, FaPlay, FaInbox, FaExclamationCircle, FaClipboardList, FaUndo } from "react-icons/fa";
import { API } from 'aws-amplify';
import { useToasts } from 'react-toast-notifications';
import Moment from 'react-moment';
import UploadFile from '../../../../components/UploadFile';
import ResizeImg from '../../../../components/ResizeImg';

import { Formik } from "formik";
import * as yup from "yup";
import { useI18n } from '../../../../i18n';

import './TaskLog.css';

const HISTORY_PREVIEW = 8;

function eventKind(note, t) {
    const text = String(note || '').trim();
    const lower = text.toLowerCase();
    if (lower === 'task completed') return { kind: 'completed', title: t('taskView.taskCompleted'), detail: '' };
    if (lower.startsWith('work done')) return { kind: 'work', title: t('taskView.workDone'), detail: text.replace(/^work done:?\s*/i, '') };
    if (lower === 'task started') return { kind: 'started', title: t('taskView.taskStarted'), detail: '' };
    if (lower === 'task accepted') return { kind: 'accepted', title: t('taskView.taskAccepted'), detail: '' };
    if (lower.startsWith('ri problem')) return { kind: 'alert', title: t('taskView.inspectionIssue'), detail: text };
    if (lower.startsWith('fault:')) return { kind: 'alert', title: t('taskView.faultReported'), detail: text.replace(/^fault:\s*/i, '') };
    if (/moved to wip|move to wip/i.test(lower)) return { kind: 'wip', title: t('taskView.movedToWip'), detail: text };
    return { kind: 'note', title: text || t('fields.note'), detail: '' };
}

function eventIcon(kind) {
    if (kind === 'completed') return <FaCheck />;
    if (kind === 'started') return <FaPlay />;
    if (kind === 'accepted') return <FaInbox />;
    if (kind === 'work') return <FaClipboardList />;
    if (kind === 'alert') return <FaExclamationCircle />;
    if (kind === 'wip') return <FaUndo />;
    return <FaRegStickyNote />;
}

function dayLabel(date) {
    if (!date) return '';
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

const TaskLog = (props) => {
    const { t } = useI18n();
    const [log, setLog] = useState([]);
    const [asset, setAsset] = useState([]);
    const { addToast } = useToasts();
    const [showNote, toggleShowNote] = useState(false);
    const [loadLog, setLoadLog] = useState(false);
    const [users, setUsers] = useState([]);
    const [file, setFile] = useState();
    const [btnDisabled, setBtnDisabled] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [preview, setPreview] = useState(null);
    const params = { 
        headers: {
            'x-api-key': process.env.REACT_APP_API_KEY,        
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
    }

    const getAssets = async () => {
        if (props.taskId) {
            await API.get('task_active','/' + props.taskId, params)
            .then(task => {         
                API.get('assets','/' + task.assetId, params)
                .then(asset => {
                    setAsset(asset);
                }).catch((err) => {
                    console.log(err)
                    addToast(t('toast.errorLoadingAssets'), { appearance: 'error' });
                });
            }).catch((err) => {
                console.log(err)
                addToast(t('toast.errorLoadingTasks'), { appearance: 'error' });
            });
        } else if (props.assetId) {
            await API.get('assets','/' + props.assetId, params)
            .then(asset => {
                setAsset(asset);
            }).catch((err) => {
                console.log(err)
                addToast(t('toast.errorLoadingAssets'), { appearance: 'error' });
            });
        }
    }

    const getUsers = async () => {
        await API.get('users','/', params)
        .then(res => {
            let userList = []; 
            res.Users.forEach((item) => {
                let user = {}
                user.id = item.Username;
                item.Attributes.forEach((e) => {
                    if (e.Name === 'name')
                        user.name = e.Value;
                }) 
                userList.push(user);                      
            })
            setUsers(userList);
        }).catch((err) => {
            console.log(err)
            addToast(t('toast.errorLoadingUsers'), { appearance: 'error' });
        });    
    }

    // Get Users from props
    useEffect(()=>{
        const loadUsers = async () => {
            await getUsers();
        }

        if (props.users) {
            setUsers(props.users);
        } else {
            loadUsers();
        }
    }, [props.users]);

    // Get Asset from props
    useEffect(()=>{
        const loadAssets = async () => {
            await getAssets();
        }

        if (props.asset) {
            setAsset(props.asset);
        } else {
            loadAssets();
        }
    }, [props.asset]);

    // Parse Task Log
    useEffect(()=>{
        if (props.taskId) {
            
            let assetLog = [];   
            asset.log && asset.log.forEach((item) => {
                if (item.taskId === props.taskId){
                    assetLog.push(item);
                }
            })
            // find users
            assetLog.forEach((item) => {
                const user = users.find(i => i.id === item.userId)
                if (user) item.user = user.name
            })
            
            //sort array desc
            assetLog.sort((a, b) => (a.date_note > b.date_note) ? 1 : -1)
            assetLog.reverse();
            setLog(assetLog);
                
        } else {
            if (props.assetId){      
                let assetLog = [];
                asset.log && asset.log.forEach((item) => {                      
                    assetLog.push(item);                                
                })

                // find users
                assetLog.forEach((item) => {
                    const user = users.find(i => i.id === item.userId)
                    if (user) item.user = user.name
                })

                //sort array desc
                assetLog.sort((a, b) => (a.date_note > b.date_note) ? 1 : -1)
                assetLog.reverse();

                setLog(assetLog);
            }
            setLoadLog(false);
        }
    }, [users, asset, loadLog])

    // Form Submit
    const handleSubmit = async (data, { resetForm }) => {
        let note = {
            taskId: props.taskId,
            userId:  props.user.username,
            text: data.note,
        }

        // upload file
        if(file) {
            const imgPath = await UploadFile('img', '/', file, 'log', null);
            note.img = imgPath;
            // console.log(imgPath)
        }

        API.post('asset_note', '/' + asset.assetId, {      
            body: note,
            headers: {
                'x-api-key': process.env.REACT_APP_API_KEY,        
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                },
        })
        .then(async res => {
            toggleShowNote(false);
            setFile();
            resetForm();
            addToast(t('toast.noteSaved'), { appearance: 'success' });
            // Re-fetch the asset so the new note appears without a page refresh
            await getAssets();
            setLoadLog(true);
        }).catch(error => {
            console.log(error)
            addToast(t('toast.errorSavingNote'), { appearance: 'error' });
        }); 
        setBtnDisabled(false);

    };

    const handleChangeFile = async (event) => {
        try {
            const file = event.target.files[0];          
            const image = await ResizeImg(file);
            setFile(image);
          } catch (err) {
            console.log(err);
          }
      }


    const schema = yup.object().shape({
        note: yup.string()
            .required(t('common.required')),
        
        });

    const initialValues = { note: '' };

    const visibleLog = historyOpen ? log : log.slice(0, HISTORY_PREVIEW);
    let lastDay = '';

    return (<div className="TaskLog">
        <div className="log-head">
            <h4>{t('taskView.siteHistory')}</h4>
            <Button size="sm" variant="outline-primary" onClick={() => toggleShowNote(!showNote)}>
                {t('common.addNote')} <FaPlus />
            </Button>
        </div>
       
        {showNote && (
        <div className="note">
        <Formik                                   
            initialValues={initialValues}
            validationSchema={schema}
            onSubmit={ (values, { setSubmitting, resetForm }) => {
                setBtnDisabled(true);
                handleSubmit(values, { resetForm });
                setSubmitting(false)
            }}
            >
            {({values, errors, touched, handleChange, handleSubmit, resetForm }) => (

        <form id='frm' onSubmit={handleSubmit}>  
            <Form.Group controlId="note" className={touched.note && errors.note ? "has-error" : null}>
                <Form.Label>{t('fields.note')}</Form.Label>
                <Form.Control 
                    as="textarea"                
                    rows="3" 
                    value={values.note} 
                    onChange={handleChange}                        
                />
                {touched.note && errors.note ? <div className="error-message">{errors.note}</div> : null}                       
            </Form.Group>
            <Form.Group controlId="file" >
                <Form.Label>{t('fields.image')}</Form.Label>
                <Form.Control 
                    as="input" 
                    value={values.file} 
                    onChange={handleChangeFile}
                    accept="image/*"
                    type="file" />
            </Form.Group>
            <div className="note-actions">
            <Button size="sm" variant="success" className="mr-1" type="submit" disabled={btnDisabled}>{t('common.save')}&nbsp;
                <FaSave />    
            </Button>  
            <Button size="sm" variant="secondary" type="reset" onClick={() => resetForm()}>{t('common.clear')}&nbsp;
                <FaTimes />    
            </Button>  
            </div>
        </form>
            )}
            </Formik> 
        </div>
        )}
        <div className="log-scroller">
        { log.length === 0 ? (
            <div className="log-empty">
                <FaRegStickyNote className="log-empty-icon" />
                <div className="log-empty-text">{t('taskView.noHistory')}</div>
                <div className="log-empty-subtext">{t('taskView.noHistoryHint')}</div>
            </div>
        ) : visibleLog.map((item, i) => {
            const event = eventKind(item.note, t);
            const day = dayLabel(item.date_note);
            const showDay = day && day !== lastDay;
            lastDay = day;
            return (
            <React.Fragment key={item.date_note + '_' + i}>
                {showDay && <div className="log-day">{day}</div>}
                <div className={`log-event is-${event.kind}${item.image_url ? ' has-media' : ''}`}>
                    <span className="log-icon">{eventIcon(event.kind)}</span>
                    <div className="log-body">
                        <strong>{event.title}</strong>
                        <span className="log-meta">{item.user || t('common.unknownUser')} · <Moment local format='DD-MM-YYYY HH:mm' date={item.date_note} /></span>
                        {event.detail ? <p>{event.detail}</p> : null}
                        {item.image_url && (
                            <button type="button" className="log-thumb" onClick={() => setPreview(item.image_url)}>
                                <img alt="" src={item.image_url} />
                            </button>
                        )}
                    </div>
                </div>
            </React.Fragment>
            );
        })
        }
        {log.length > HISTORY_PREVIEW && (
            <button type="button" className="log-more" onClick={() => setHistoryOpen((open) => !open)}>
                {historyOpen ? 'Show less' : `View full history (${log.length})`}
            </button>
        )}
        </div>
        {preview && (
            <div className="log-lightbox" onClick={() => setPreview(null)} role="presentation">
                <img src={preview} alt="" />
            </div>
        )}
        </div>
        )
	
}
const mapStateToProps = state => {
    return {
        user: state.auth.user,
    };
};

export default connect(mapStateToProps )(TaskLog);


